// Side-view non-muscle structure layer for pain capture
// (PAIN_TRACKING_PLAN.md Task 2.2). React-free / JSX-free (hazard H7).
//
// Same 220x520 canvas as regionsSide.ts. The figure faces screen-LEFT, so
// anterior is low x and posterior is high x. As in regionsSide.ts nothing here
// is mirrored: this view draws the near side only, so every region is a single
// unpaired "-l" region (spine segments, which are midline, use centerPainRegion
// and a "-c" id). Validator rule S4 (both halves in a view) only applies to
// front / back, matching muscle-map rule R4's side-view allowance.
//
// Coverage (Task 2.2): shoulder / elbow / hip / knee / ankle joints; TMJ; the
// full spine column; sciatic track; peroneal + tibial nerves.

import type { PainStructure } from "@medassist/shared";
import { PAIN_STRUCTURE_META } from "@medassist/shared";
import type { PainRegion } from "./painRegions.js";
import { centerPainRegion, ellipsePath } from "./painRegions.js";

/** One near-side-only region (regionsSide.ts's nearRegion convention). kind is
 *  taken from PAIN_STRUCTURE_META so it can never disagree with the taxonomy. */
function near(structure: PainStructure, d: string): PainRegion {
  return {
    id: `${structure}-l`,
    structure,
    kind: PAIN_STRUCTURE_META[structure].kind,
    side: "left",
    d,
  };
}

export const SIDE_STRUCTURES: PainRegion[] = [
  // ── joints ─────────────────────────────────────────────────────────────────
  near("joint-shoulder", ellipsePath(118, 116, 9, 10)),
  near("joint-elbow", ellipsePath(120, 190, 9, 9)),
  near("joint-hip", ellipsePath(122, 256, 10, 11)),
  near("joint-knee", ellipsePath(114, 364, 11, 12)),
  near("joint-ankle", ellipsePath(116, 481, 8, 8)),
  near("joint-tmj", ellipsePath(124, 52, 5, 6)),

  // ── spine column (posterior strip, following the S-curve of the back) ──────
  centerPainRegion("spine-c", ellipsePath(135, 90, 5, 12)),
  centerPainRegion("spine-t", ellipsePath(150, 150, 5, 42)),
  centerPainRegion("spine-l1", ellipsePath(150, 200, 5, 6)),
  centerPainRegion("spine-l2", ellipsePath(149, 213, 5, 6)),
  centerPainRegion("spine-l3", ellipsePath(148, 226, 5, 6)),
  centerPainRegion("spine-l4", ellipsePath(148, 239, 5, 6)),
  centerPainRegion("spine-l5", ellipsePath(149, 252, 5, 6)),
  centerPainRegion("spine-s", ellipsePath(153, 270, 6, 13)),

  // ── nerve tracks ─────────────────────────────────────────────────────────
  // Sciatic: buttock down the back of the thigh to the knee.
  near(
    "nerve-sciatic",
    "M 156 248 C 154 264 150 286 145 312 C 141 334 139 352 139 362 C 141 367 145 367 146 360 C 148 346 150 328 154 306 C 158 286 161 266 162 252 C 162 247 158 244 156 248 Z",
  ),
  // Common peroneal: winds around the outside of the knee toward the top of the
  // foot.
  near(
    "nerve-peroneal",
    "M 129 362 C 126 378 122 398 118 424 C 114 452 110 476 104 496 C 103 500 108 502 110 498 C 115 478 119 452 123 426 C 127 400 131 378 133 366 C 134 361 130 358 129 362 Z",
  ),
  // Tibial: behind the inner ankle, down toward the sole.
  near(
    "nerve-tibial",
    "M 133 450 C 131 460 129 470 129 480 C 129 490 127 500 123 506 C 121 509 126 511 128 506 C 132 498 134 488 135 478 C 136 468 137 458 137 450 C 136 447 133 447 133 450 Z",
  ),
];
